import { RouteLocationNormalizedLoaded } from 'vue-router';

import { useAppointmentModuleSearchStore } from 'src/stores/appointmentModuleSearch';
import { useDocumentListStore } from 'src/stores/documentList';
import { useFilterListStore } from 'src/stores/filterList';
import { useGenericActivityListStore } from 'src/stores/genericActivityList';
import { useIdoActivityHistoryListStore } from 'src/stores/idoActivityHistoryList';
import { useMailListStore } from 'src/stores/mailList';
import { useMessageListStore } from 'src/stores/messageList';
import { useNoteListStore } from 'src/stores/noteList';
import { usePhoneCallListStore } from 'src/stores/phoneCallList';
import { useTaskListStore } from 'src/stores/taskList';
import { useTrashListStore } from 'src/stores/trashList';

import { MODULE_LIST_TYPE } from 'src/models/genericActivityList';

import { hideTheLoadingIndicator } from 'src/util/common';

type ViewMode = MODULE_LIST_TYPE.ALL | MODULE_LIST_TYPE.MINE | MODULE_LIST_TYPE.OPEN;
type ViewActionMap = Record<ViewMode, Array<string>>;

const dialogHidingActionsByModule: Record<string, ViewActionMap | null> = {
	agendaList: {
		ALL: ['fetchAllResults'],
		MINE: ['fetchMineResults'],
		OPEN: [],
	},
	document: null,
	documentList: {
		ALL: ['fetchAllDocumentList'],
		MINE: ['fetchMineDocumentList'],
		OPEN: ['fetchOpenDocumentList'],
	},
	filter: null,
	filterList: {
		ALL: ['fetchFilterList'],
		MINE: ['fetchFilterList'],
		OPEN: ['fetchFilterList'],
	},
	mail: null,
	mailList: {
		ALL: ['fetchAllMailList'],
		MINE: ['fetchMineMailList'],
		OPEN: ['fetchOpenMailList'],
	},
	message: null,
	messageList: {
		ALL: ['fetchAllMessageList'],
		MINE: ['fetchMineMessageList'],
		OPEN: ['fetchOpenMessageList'],
	},
	note: null,
	noteList: {
		ALL: ['fetchAllNoteList'],
		MINE: ['fetchMineNoteList'],
		OPEN: ['fetchOpenNoteList'],
	},
	phoneCall: null,
	phoneCallList: {
		ALL: ['fetchAllPhoneCallList'],
		MINE: ['fetchMinePhoneCallList'],
		OPEN: ['fetchOpenPhoneCallList'],
	},
	record: null,
	task: null,
	taskList: {
		ALL: ['fetchAllTaskList'],
		MINE: ['fetchMineTaskList'],
		OPEN: ['fetchOpenTaskList'],
	},
	trash: null,
	trashList: {
		ALL: ['fetchAllTrashList'],
		MINE: ['fetchMineTrashList'],
		OPEN: ['fetchOpenTrashList'],
	},
};

const idoHistoryTriggerActions = new Set([
	'fetchIdoActivityHistoryList',
	'fetchIdoActivityHistoryListByIdoAndInstanceId',
]);

const resolveListTypeForAction = (moduleName: string, actionName: string): MODULE_LIST_TYPE | null => {
	const actionMap = dialogHidingActionsByModule[moduleName];
	if (!actionMap) {
		return null;
	}

	if (actionMap.OPEN.includes(actionName)) return MODULE_LIST_TYPE.OPEN;
	if (actionMap.MINE.includes(actionName)) return MODULE_LIST_TYPE.MINE;
	if (actionMap.ALL.includes(actionName)) return MODULE_LIST_TYPE.ALL;

	return null;
};

const shouldHideIndicatorForListAction = (
	moduleName: string,
	actionName: string,
	activeView: MODULE_LIST_TYPE
): boolean => {
	const actionMap = dialogHidingActionsByModule[moduleName];
	if (actionMap === null || actionMap === undefined) {
		return idoHistoryTriggerActions.has(actionName);
	}

	const normalizedView: ViewMode =
		activeView === MODULE_LIST_TYPE.ALL ||
		activeView === MODULE_LIST_TYPE.MINE ||
		activeView === MODULE_LIST_TYPE.OPEN
			? activeView
			: MODULE_LIST_TYPE.OPEN;

	return actionMap[normalizedView].includes(actionName);
};

export const loadingStatePlugin = (route: RouteLocationNormalizedLoaded): void => {
	const genericActivityListStore = useGenericActivityListStore();

	const registerLoadingAction = (
		moduleName: string,
		registerAction: (handler: (actionName: string) => void) => void
	): void => {
		registerAction((actionName: string) => {
			if (typeof route.name !== 'string') {
				return;
			}

			if (route.name !== genericActivityListStore.activeModule || route.name !== moduleName) {
				return;
			}

			if (
				shouldHideIndicatorForListAction(
					moduleName,
					actionName,
					genericActivityListStore.getActiveView(moduleName)
				)
			) {
				hideTheLoadingIndicator();
			}
		});
	};

	const registerStoreActionHook = (
		moduleName: string,
		store: {
			$onAction: (
				callback: (context: {
					after: (callback: () => void) => void;
					name: string;
					onError: (callback: (error: unknown) => void) => void;
				}) => void
			) => void;
		}
	): void => {
		registerLoadingAction(moduleName, (handler) => {
			store.$onAction(({ after, name, onError }) => {
				// The per-lane loading spinner reads the token-paired fetching state directly
				// (isFetchingScope); this hook only settles the loaded bookkeeping.
				const settleLoadingView = (): void => {
					const listType = resolveListTypeForAction(moduleName, name);
					if (listType !== null) {
						genericActivityListStore.markListLoaded(moduleName, listType);
					}
				};
				after(() => {
					handler(name);
					settleLoadingView();
				});
				onError(() => {
					settleLoadingView();
				});
			});
		});
	};

	registerStoreActionHook('agendaList', useAppointmentModuleSearchStore());
	registerStoreActionHook('documentList', useDocumentListStore());
	registerStoreActionHook('filterList', useFilterListStore());
	registerStoreActionHook('mailList', useMailListStore());
	registerStoreActionHook('messageList', useMessageListStore());
	registerStoreActionHook('noteList', useNoteListStore());
	registerStoreActionHook('phoneCallList', usePhoneCallListStore());
	registerStoreActionHook('taskList', useTaskListStore());
	registerStoreActionHook('trashList', useTrashListStore());
	registerStoreActionHook('record', useIdoActivityHistoryListStore());
	registerStoreActionHook('document', useIdoActivityHistoryListStore());
	registerStoreActionHook('filter', useIdoActivityHistoryListStore());
	registerStoreActionHook('mail', useIdoActivityHistoryListStore());
	registerStoreActionHook('message', useIdoActivityHistoryListStore());
	registerStoreActionHook('note', useIdoActivityHistoryListStore());
	registerStoreActionHook('phoneCall', useIdoActivityHistoryListStore());
	registerStoreActionHook('task', useIdoActivityHistoryListStore());
	registerStoreActionHook('trash', useIdoActivityHistoryListStore());
};
